/**
 * Identity Apollo Client — Gateway-specific client for identity operations
 *
 * This client targets the federation gateway API directly
 * (NEXT_PUBLIC_GATEWAY_API_ENDPOINT) for identity operations.
 *
 * Used by identity mutation hooks to route GraphQL operations through
 * the correct endpoint (Client → API Gateway → Auth Service).
 *
 * This is separate from the main Apollo client (module-core) because:
 * - Default client targets Magento commerce GraphQL
 * - Identity operations target the federation gateway (Auth Service subgraph)
 *
 * Uses shared createAuthLink (store code, API key, token) and createErrorLink
 * from module-core to ensure consistent headers across all clients.
 */
import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, type NormalizedCacheObject } from '@apollo/client';
import { GRAPHQL_PROXY_ENDPOINTS, createAuthLink, createErrorLink } from '@kidslabs/module-core';

let identityClient: ApolloClient<NormalizedCacheObject> | null = null;

const createIdentityHttpLink = () => {
    return new HttpLink({
        uri: GRAPHQL_PROXY_ENDPOINTS.gateway,
        credentials: 'same-origin',
    });
};

const createIdentityClient = (): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient({
        // Cast links to ApolloLink to resolve duplicate @apollo/client type versions across packages
        link: ApolloLink.from([
            createAuthLink() as unknown as ApolloLink,
            createErrorLink() as unknown as ApolloLink,
            createIdentityHttpLink(),
        ]),
        cache: new InMemoryCache(),
        defaultOptions: {
            mutate: {
                errorPolicy: 'all',
            },
        },
    });
};

/**
 * Get or create the Identity Apollo Client singleton.
 * This client is configured to use the federation gateway endpoint.
 */
export const getIdentityClient = (): ApolloClient<NormalizedCacheObject> => {
    if (!identityClient) {
        identityClient = createIdentityClient();
    }
    return identityClient;
};

/**
 * Reset the identity client — clears cache and destroys the singleton.
 * Called during logout to ensure no stale identity data remains.
 */
export const resetIdentityClient = (): void => {
    if (identityClient) {
        identityClient.clearStore();
        identityClient = null;
    }
};
